import { useState, useEffect } from 'react';
import axios from 'axios';
import { getCachedValue, setCachedValue } from '../utils/requestCache';
import { checkRateLimit } from '../utils/requestRateLimiter';
import {
  getInFlightRequest,
  setInFlightRequest,
  retainInFlightRequest,
  releaseInFlightRequest,
  settleInFlightRequest
} from '../utils/inFlightRequests';

const RATE_LIMIT_MESSAGE = 'Too many requests. Please wait a moment and try again.';

const useAddressValidationBase = ({
  isEnabled,
  cacheNamespace = null,
  cacheTtlMs = null,
  rateLimitNamespace,
  rateLimitConfig,
  requestKey,
  requestUrl,
  requestParams,
  transformResponse,
  effectDeps = []
}) => {
  const requestCycleKey = JSON.stringify({
    isEnabled,
    effectDeps
  });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [activeCycleKey, setActiveCycleKey] = useState(requestCycleKey);

  const isPendingCycleChange = activeCycleKey !== requestCycleKey;

  useEffect(() => {
    setActiveCycleKey(requestCycleKey);

    if (!isEnabled) {
      setRateLimited(false);
      return;
    }

    let cancelled = false;
    let activeRequest = null;
    let activeRequestKey = null;

    const validateAddress = async () => {
      setLoading(true);
      setError(null);
      setSuggestions([]);

      try {
        const cached = cacheNamespace ? getCachedValue(cacheNamespace, requestKey) : null;
        if (cached) {
          if (!cancelled) {
            setRateLimited(false);
            setSuggestions(cached.suggestions);
            setLoading(false);
          }
          return;
        }

        const rateLimitStatus = checkRateLimit(rateLimitNamespace, rateLimitConfig);
        if (!rateLimitStatus.allowed) {
          setRateLimited(true);
          setError(RATE_LIMIT_MESSAGE);
          setLoading(false);
          return;
        }

        activeRequestKey = requestKey;
        const existingRequest = getInFlightRequest(activeRequestKey);
        let response;

        if (existingRequest) {
          activeRequest = retainInFlightRequest(activeRequestKey);
          ({ response } = await activeRequest.promise);
        } else {
          const controller = new AbortController();
          const request = {
            controller,
            settled: false,
            promise: (async () => {
              const networkResponse = await axios.get(requestUrl, {
                params: requestParams,
                signal: controller.signal
              });
              return { response: networkResponse };
            })()
          };

          setInFlightRequest(activeRequestKey, request);
          activeRequest = retainInFlightRequest(activeRequestKey);

          try {
            ({ response } = await activeRequest.promise);
          } finally {
            settleInFlightRequest(activeRequestKey, activeRequest);
          }
        }

        if (cancelled) {
          return;
        }

        const transformedSuggestions = transformResponse(response.data);
        if (cacheNamespace) {
          setCachedValue(cacheNamespace, requestKey, {
            suggestions: transformedSuggestions
          }, cacheTtlMs || undefined);
        }

        setRateLimited(false);
        setSuggestions(transformedSuggestions);
      } catch (err) {
        if (cancelled || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return;
        }
        setRateLimited(false);
        setError(err.response?.data?.message || err.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    validateAddress();

    return () => {
      cancelled = true;
      if (activeRequestKey && activeRequest) {
        releaseInFlightRequest(activeRequestKey, activeRequest);
      }
      if (activeRequest?.subscribers === 0 && !activeRequest?.settled) {
        activeRequest.controller.abort();
      }
    };
  }, [isEnabled, requestCycleKey]);

  return {
    suggestions: isPendingCycleChange ? [] : suggestions,
    loading: isPendingCycleChange ? Boolean(isEnabled) : loading,
    error: isPendingCycleChange ? null : error,
    rateLimited: isPendingCycleChange ? false : rateLimited
  };
};

export default useAddressValidationBase;
